import React from 'react';
import type { PartnerStatus } from '@/types/auth';

export interface StatusBadgeProps {
  status: PartnerStatus | string;
  label?: string;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; border: string; dot: string }
> = {
  active: {
    label: 'Active',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
  },
  good: {
    label: 'Good Standing',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
  },
  at_risk: {
    label: 'At Risk',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-400 animate-pulse',
  },
  suspended: {
    label: 'Suspended',
    bg: 'bg-rose-500/10',
    text: 'text-rose-400',
    border: 'border-rose-500/30',
    dot: 'bg-rose-400',
  },
  dormant: {
    label: 'Dormant',
    bg: 'bg-zinc-800/60',
    text: 'text-zinc-400',
    border: 'border-zinc-700/50',
    dot: 'bg-zinc-500',
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  size = 'sm',
  className = '',
}) => {
  const normalizedKey = status.toLowerCase();
  const config = STATUS_CONFIG[normalizedKey] || {
    label: status,
    bg: 'bg-zinc-800/60',
    text: 'text-zinc-300',
    border: 'border-zinc-700/50',
    dot: 'bg-zinc-400',
  };

  const displayLabel = label || config.label;

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-1',
    sm: 'text-xs px-2 py-0.5 gap-1.5',
    md: 'text-sm px-2.5 py-1 gap-2',
  }[size];

  const dotSize = {
    xs: 'w-1 h-1',
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
  }[size];

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${config.bg} ${config.text} ${config.border} ${sizeClasses} ${className}`}
    >
      <span className={`rounded-full shrink-0 ${config.dot} ${dotSize}`} />
      <span className="capitalize">{displayLabel}</span>
    </span>
  );
};

export default StatusBadge;
